// The VPN's flow records and the settings that govern them (docs/VpnAccess.md,
// "Slice 5 design"). SaaS-only: the trial build keeps no flow records, and this
// module is aliased to a stand-in there (vite.edition.ts).
import axios from 'axios';
import { config } from '@/config';

const api = (path: string) => `${config.backendApi}/api${path}`;
const opts = { withCredentials: true } as const;

/** One connection: its final record, or its latest interim one. Every field
 *  but the ID may be missing from an older record. */
export interface FlowRecord {
  flowId: string;
  gatewayId: string | null;
  /** The IP protocol number: 6 TCP, 17 UDP, 1 ICMP. */
  protocol: number | null;
  clientAddress: string | null;
  clientPort: number | null;
  destinationSent: string | null;
  destinationReached: string | null;
  destinationPort: number | null;
  /** The gateway's address and port as the site saw them. */
  gatewayAddress: string | null;
  gatewayPort: number | null;
  /** none, ipv4_alias, four_via_six, or a number this build doesn't know. */
  translation: string | null;
  bytesFromClient: number | null;
  packetsFromClient: number | null;
  bytesToClient: number | null;
  packetsToClient: number | null;
  start: string | null;
  end: string | null;
  /** The connection had ended; otherwise its counts so far. */
  ended: boolean | null;
}

export interface FlowPage {
  flows: FlowRecord[];
  total: number;
  page: number;
  pageSize: number;
}

export async function fetchSessionFlows(sessionId: string, page: number, pageSize: number): Promise<FlowPage> {
  const response = await axios.get<FlowPage>(api(`/vpn/sessions/${sessionId}/flows`), {
    ...opts,
    params: { page, pageSize },
  });
  return response.data;
}

export interface FlowLookup {
  /** The region the connection was in. */
  regionId: string;
  /** RFC 3339, UTC. */
  at: string;
  /** The address the site saw it come from: one of the region's gateways. */
  address?: string;
  destination?: string;
  port?: number;
}

/** A flow matching a lookup, with whose session it was. */
export interface FlowMatch {
  sessionId: string;
  userId: string;
  userName: string | null;
  userEmail: string | null;
  clientDeviceId: string;
  clientDeviceName: string | null;
  flow: FlowRecord;
}

export async function lookupFlows(query: FlowLookup): Promise<FlowMatch[]> {
  const params: Record<string, string> = { regionId: query.regionId, at: query.at };
  if (query.address) params.address = query.address;
  if (query.destination) params.destination = query.destination;
  if (query.port !== undefined) params.port = String(query.port);
  const response = await axios.get<{ matches: FlowMatch[] }>(api('/vpn/flows'), { ...opts, params });
  return response.data.matches;
}

export interface VpnSettings {
  flowRetentionDays: number;
  /** Whether that is the default, not the organization's own choice. */
  flowRetentionIsDefault: boolean;
}

export async function fetchVpnSettings(): Promise<VpnSettings> {
  const response = await axios.get<VpnSettings>(api('/vpn/settings'), opts);
  return response.data;
}

/** `null` returns to the default. */
export async function setFlowRetention(days: number | null): Promise<VpnSettings> {
  const response = await axios.put<VpnSettings>(api('/vpn/settings'), { flowRetentionDays: days }, opts);
  return response.data;
}

export const MAX_FLOW_RETENTION_DAYS = 3650;
